import { useState } from 'react';
import { SplitLayout } from './SplitLayout';
import { categories } from '../data/tools';
import { useCatalog } from '../context/CatalogContext';
import { ToolCard } from './ToolCard';
import { SuggestToolModal } from './SuggestToolModal';
import { ModeSwitcher } from './ModeSwitcher';
import { useModeContext } from '../context/ModeContext';
import { AssistantChat } from './AssistantChat';
import { AppLogo } from './AppLogo';

export const SolutionSearch = () => {
  const { mode } = useModeContext();
  const { allTools: tools } = useCatalog();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // HIPAA compatibility is the top-tier filter for clinicians — default ON.
  const [hipaaOnly, setHipaaOnly] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const toolsPerPage = 10;

  // Filter tools based on selected category, HIPAA filter, and search query
  const filteredTools = tools.filter(t => {
    const matchesCategory = !selectedCategory || t.category === selectedCategory;
    const matchesHipaa = !hipaaOnly || t.hipaaCompliant;
    const matchesSearch = !searchQuery || t.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesHipaa && matchesSearch;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredTools.length / toolsPerPage);
  const startIndex = (currentPage - 1) * toolsPerPage;
  const endIndex = startIndex + toolsPerPage;
  const paginatedTools = filteredTools.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  const handleFilterChange = (callback: () => void) => {
    callback();
    setCurrentPage(1);
  };

  return (
    <div className="max-w-[1800px] mx-auto px-6 pt-4 pb-6 font-sans">
      {/* Header */}
      <div className="text-center space-y-1 mb-4">
        <div className="flex justify-center mb-2">
          <AppLogo sizeClassName="w-14 h-14" className="rounded-btn" />
        </div>
        <h1 className="text-2xl font-bold text-neutral-800 tracking-tight font-heading">Athen.ai</h1>
        <p className="text-neutral-500 text-sm mb-8">Your Personal Healthcare AI Consultant</p>
        <div className="flex justify-center">
          <div className="w-64">
            <ModeSwitcher />
          </div>
        </div>
      </div>

      {mode === 'consultant' ? (
        <>
          {/* Split Layout: Chat (left) + Tabs (right) */}
          <SplitLayout />

          {/* Browse Tools Section */}
          <div id="browse-tools" className="pt-8 border-t border-neutral-200 mt-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-neutral-800 font-heading">Browse All Tools</h2>
              <button
                onClick={() => setShowSuggest(true)}
                className="btn-secondary inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Suggest A Tool
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Filters Panel (Left) */}
              <div className="lg:col-span-3 space-y-6">
                {/* HIPAA Filter — the top-tier filter for clinicians */}
                <div className="bg-emerald-50/60 border border-emerald-200 rounded-card p-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hipaaOnly}
                      onChange={(e) => handleFilterChange(() => setHipaaOnly(e.target.checked))}
                      className="w-5 h-5 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <span className="font-medium text-neutral-800">HIPAA Compliant Only</span>
                      <p className="text-xs text-neutral-500">Safe for patient data (BAA available)</p>
                    </div>
                  </label>
                  <div className="mt-3 pt-3 border-t border-emerald-100 space-y-1">
                    <p className="flex items-center gap-2 text-xs text-neutral-600">
                      <span className="hipaa-dot hipaa-dot-yes" /> HIPAA compliant — OK for patient data
                    </p>
                    <p className="flex items-center gap-2 text-xs text-neutral-600">
                      <span className="hipaa-dot hipaa-dot-no" /> Not verified — no patient data
                    </p>
                  </div>
                </div>

                {/* Search Filter */}
                <div className="bg-neutral-50 border border-neutral-200 rounded-card p-4">
                  <label className="block">
                    <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2 block">Search Tools</span>
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleFilterChange(() => setSearchQuery(e.target.value))}
                        placeholder="Search All Tools..."
                        className="input-base pl-10"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => handleFilterChange(() => setSearchQuery(''))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </label>
                </div>

                {/* Category Filters */}
                <div className="bg-neutral-50 border border-neutral-200 rounded-card p-4">
                  <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-widest mb-3">Categories</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleFilterChange(() => setSelectedCategory(null))}
                      className={`w-full text-left px-3 py-2 rounded-btn text-sm font-medium transition-all ${
                        selectedCategory === null
                          ? 'bg-primary-600 text-white'
                          : 'text-neutral-600 hover:bg-neutral-100'
                      }`}
                    >
                      All Tools
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleFilterChange(() => setSelectedCategory(cat.id))}
                        className={`w-full text-left px-3 py-2 rounded-btn text-sm transition-all ${
                          selectedCategory === cat.id
                            ? 'bg-primary-600 text-white'
                            : 'text-neutral-600 hover:bg-neutral-100'
                        }`}
                      >
                        <span className="font-medium">{cat.label}</span>
                        <span className="block text-xs opacity-75">{cat.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="text-sm text-neutral-500 px-2">
                  <p>
                    Tools Shown: {filteredTools.length}
                  </p>
                  <p>
                    HIPAA Compliant Total: {tools.filter(t => t.hipaaCompliant).length}
                  </p>
                </div>
              </div>

              {/* Tools Grid (Right) */}
              <div className="lg:col-span-9">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {paginatedTools.map((tool) => (
                    <ToolCard key={tool.id} tool={tool} />
                  ))}
                </div>
                {filteredTools.length === 0 && (
                  <div className="text-center py-12 text-neutral-400">
                    <p>No tools match your filters</p>
                    <button
                      onClick={() => {
                        setSelectedCategory(null);
                        setHipaaOnly(false);
                        setSearchQuery('');
                        setCurrentPage(1);
                      }}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium mt-2"
                    >
                      Clear filters
                    </button>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-2 rounded-btn text-sm font-medium transition-colors ${
                        currentPage === 1
                          ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                          : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300'
                      }`}
                    >
                      Previous
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        // Show first page, last page, current page, and pages around current
                        const showPage = page === 1 ||
                                        page === totalPages ||
                                        Math.abs(page - currentPage) <= 1;

                        if (!showPage && page === 2 && currentPage > 3) {
                          return <span key={page} className="px-2 text-neutral-400">...</span>;
                        }
                        if (!showPage && page === totalPages - 1 && currentPage < totalPages - 2) {
                          return <span key={page} className="px-2 text-neutral-400">...</span>;
                        }
                        if (!showPage) return null;

                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-10 h-10 rounded-btn text-sm font-medium transition-colors ${
                              currentPage === page
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-2 rounded-btn text-sm font-medium transition-colors ${
                        currentPage === totalPages
                          ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                          : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <AssistantChat />
      )}

      {showSuggest && <SuggestToolModal onClose={() => setShowSuggest(false)} />}
    </div>
  );
};
